// Раздел «Себестоимость»: вкладки «Обзор» | «ABC-анализ» | «Склад». Вкладка — в адресе (/cost?tab=abc), как на «Кухне».
// «Склад» (остатки iikoCloud) — только при подключённом iikoCloud у активного сервера (iikocloud — useIikoCloudStatus):
// без него две вкладки, а /cost?tab=stock открывает «Обзор». Пока признак неизвестен (null), открытая по ссылке
// вкладка «Склад» ждёт его, а не перескакивает на «Обзор».
// Вкладка монтируется при первом открытии и дальше остаётся смонтированной (неактивная скрыта): ABC не грузится,
// пока его не открыли, а переключение туда-обратно не перезапрашивает данные и не сбрасывает фильтры и прокрутку.
// Период общий для вкладок «Обзор» и «ABC» (здесь); выбор ресторана — общий для приложения (шапка).
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import SegmentedTabs, { panelDomId, tabDomId } from '../../components/SegmentedTabs';
import { getScrollParent } from '../../components/StickyFiltersBar';
import { SECTION_ACCENTS } from '../../styles/tokens';
import CostOverviewTab from './CostOverviewTab';
import AbcTab from './AbcTab';
import StockTab from './StockTab';

const ACCENT = SECTION_ACCENTS['/cost'];
const TABS = [
  { value: 'overview', label: 'Обзор', icon: BarChartRoundedIcon },
  { value: 'abc', label: 'ABC-анализ', icon: LeaderboardOutlinedIcon },
];
const STOCK_TAB = { value: 'stock', label: 'Склад', icon: Inventory2OutlinedIcon };
// Три вкладки с иконками на 360 px не влезают в строку — на узких экранах только подписи
const STOCK_ICONS_FROM = 400;
const PANELS = 'cost-panel';
// У каждой вкладки своя копия переключателя (в её закреплённой панели) — и свои id вкладок
const tabsId = (owner) => `cost-tabs-${owner}`;

export default function CostPage({ iikocloud = false, ...props }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const withStock = iikocloud === true || (iikocloud === null && requested === 'stock');
  const tabList = withStock ? [...TABS, STOCK_TAB] : TABS;
  const tab = tabList.some((t) => t.value === requested) ? requested : 'overview';
  const [period, setPeriod] = useState(() => localStorage.getItem('selectedPeriod') || 'today');
  const [visited, setVisited] = useState(() => new Set([tab]));
  const rootRef = useRef(null);
  const scrollRef = useRef({}); // прокрутка по вкладкам
  if (!visited.has(tab)) setVisited(new Set([...visited, tab]));

  // Прокручивается контейнер AppLayout (у html, body и #root overflow: hidden); нет его — окно
  const scroller = () => getScrollParent(rootRef.current);
  const scrollTop = () => scroller()?.scrollTop ?? window.scrollY;

  const changeTab = (value) => {
    if (value === tab) return;
    scrollRef.current[tab] = scrollTop();
    const next = new URLSearchParams(searchParams);
    if (value === 'overview') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next);
  };

  // Вернуть прокрутку вкладки (первое открытие — сверху)
  useLayoutEffect(() => {
    const top = scrollRef.current[tab] || 0;
    const element = getScrollParent(rootRef.current);
    if (element) element.scrollTop = top;
    else window.scrollTo(0, top);
  }, [tab]);

  const changePeriod = (value) => {
    setPeriod(value);
    localStorage.setItem('selectedPeriod', value);
  };

  const tabs = (owner) => (
    <SegmentedTabs
      id={tabsId(owner)}
      controls={PANELS}
      tabs={tabList}
      value={tab}
      onChange={changeTab}
      accent={ACCENT}
      label="Раздел «Себестоимость»"
      iconsFrom={withStock ? STOCK_ICONS_FROM : 0}
    />
  );
  const panel = (value) => ({
    role: 'tabpanel',
    id: panelDomId(PANELS, value),
    'aria-labelledby': tabDomId(tabsId(value), value),
    sx: { display: tab === value ? 'flex' : 'none', flexDirection: 'column', width: '100%' },
  });

  return (
    <Box ref={rootRef} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {visited.has('overview') && (
        <Box {...panel('overview')}>
          <CostOverviewTab {...props} iikocloud={iikocloud} period={period} onPeriodChange={changePeriod} tabs={tabs('overview')} />
        </Box>
      )}
      {visited.has('abc') && (
        <Box {...panel('abc')}>
          <AbcTab {...props} period={period} onPeriodChange={changePeriod} tabs={tabs('abc')} />
        </Box>
      )}
      {withStock && visited.has('stock') && (
        <Box {...panel('stock')}>
          <StockTab {...props} available={iikocloud === true} tabs={tabs('stock')} />
        </Box>
      )}
    </Box>
  );
}
